import apiClient from "./apiClient";
import {
  VehicleServiceUpdateRequest,
  VehicleServiceUpdateResponse,
  RideAlongRequest,
  RideAlongResponse,
  OvertimeRequestSubmission,
  OvertimeRequestResponse,
  OpenShiftsRequest,
  OpenShiftsResponse,
  ShiftClosureRequest,
  ShiftClosureResponse,
  ReceiptSubmissionRequest,
  ReceiptSubmissionResponse,
  FuelCardSubmission,
  FuelCardResponse,
  ShiftOverlapAuthorizationRequest,
  ShiftOverlapAuthorizationResponse,
} from "forms/models";
import { KeyValue } from "common/models";

const submitVehicleServiceUpdate = async (
  request: VehicleServiceUpdateRequest
): Promise<VehicleServiceUpdateResponse> => {
  return apiClient.post("/forms/vehicle-service-update", request);
};

const submitRideAlong = async (request: RideAlongRequest): Promise<RideAlongResponse> => {
  return apiClient.post("/forms/ride-along", request);
};

const submitOvertimeRequest = async (
  request: OvertimeRequestSubmission
): Promise<OvertimeRequestResponse> => {
  return apiClient.post("/forms/overtime-request", request);
};

const getOpenShifts = async (request: OpenShiftsRequest): Promise<OpenShiftsResponse> => {
  return apiClient.post("/forms/shifts/open", request);
};

const getShiftTypes = async (): Promise<KeyValue[]> => {
  return apiClient.get("/lookup/shift-types");
};

const submitShiftClosure = async (
  request: ShiftClosureRequest,
  files: File[]
): Promise<ShiftClosureResponse> => {
  const formData = new FormData();
  formData.append("yardDailyID", request.yardDailyID.toString());
  formData.append("shiftTypeID", request.shiftTypeID.toString());
  formData.append("driverID", request.driverID.toString());
  formData.append("vehicleID", request.vehicleID.toString());
  formData.append("startTime", request.startTime);
  formData.append("endTime", request.endTime);
  formData.append("begMileage", request.begMileage.toString());
  formData.append("endMileage", request.endMileage.toString());
  formData.append("tripsRan", request.tripsRan.toString());
  formData.append("locationName", request.locationName);
  formData.append("shiftDate", request.shiftDate);
  formData.append("shiftName", request.shiftName);
  formData.append("vanNumber", request.vanNumber);
  files.forEach((file) => {
    formData.append("files", file);
  });
  return apiClient.post("/forms/shifts/close", formData);
};

const submitReceipt = async (
  request: ReceiptSubmissionRequest,
  receiptPictures: File[]
): Promise<ReceiptSubmissionResponse> => {
  const formData = new FormData();
  formData.append("locationId", request.locationId.toString());
  formData.append("locationName", request.locationName);
  formData.append("employeeId", request.employeeId.toString());
  formData.append("employeeName", request.employeeName);
  formData.append("employeeNumber", request.employeeNumber);
  formData.append("isBargeReceipt", request.isBargeReceipt);
  formData.append("isBillable", request.isBillable);
  formData.append("tripNumber", request.tripNumber);
  formData.append("purchaseType", request.purchaseType);
  formData.append("purchaseDate", request.purchaseDate);
  formData.append("totalAmount", request.totalAmount);
  receiptPictures.forEach((file) => {
    formData.append("receiptPictures", file);
  });
  return apiClient.post("/forms/receipt-submission", formData);
};

const submitFuelCard = async (
  request: FuelCardSubmission,
  receiptPictures: File[] = []
): Promise<FuelCardResponse> => {
  const formData = new FormData();
  formData.append("actionItem", request.actionItem);
  if (request.vehicleLocationId !== null) {
    formData.append("vehicleLocationId", request.vehicleLocationId.toString());
  }
  formData.append("vehicleLocationName", request.vehicleLocationName);
  if (request.unitNumber !== null) {
    formData.append("unitNumber", request.unitNumber.toString());
  }
  formData.append("tripShiftNumber", request.tripShiftNumber);
  formData.append("reasonForReset", request.reasonForReset);
  formData.append("reasonForPurchase", request.reasonForPurchase);
  formData.append("purchaseDateTime", request.purchaseDateTime);
  formData.append("purchaseAmount", request.purchaseAmount);
  formData.append("replaceReason", request.replaceReason);
  formData.append("employeeName", request.employeeName);
  formData.append("employeeNumber", request.employeeNumber);
  receiptPictures.forEach((file) => {
    formData.append("receiptPictures", file);
  });
  return apiClient.post("/forms/fuel-card", formData);
};

const submitShiftOverlapAuthorization = async (
  request: ShiftOverlapAuthorizationRequest,
  authorizationPhotos: File[] = []
): Promise<ShiftOverlapAuthorizationResponse> => {
  const formData = new FormData();
  formData.append("dateOfOverlap", request.dateOfOverlap);
  formData.append("locationId", request.locationId.toString());
  formData.append("locationName", request.locationName);
  formData.append("employeeId", request.employeeId.toString());
  formData.append("employeeName", request.employeeName);
  formData.append("employeeNumber", request.employeeNumber);
  formData.append("shiftNumber", request.shiftNumber);
  formData.append("scheduledStartTime", request.scheduledStartTime);
  formData.append("scheduledEndTime", request.scheduledEndTime);
  formData.append("actualEndTime", request.actualEndTime);
  formData.append("overlapMinutes", request.overlapMinutes.toString());
  formData.append("reasonForOverlap", request.reasonForOverlap);
  formData.append("authorizedOfficialName", request.authorizedOfficialName);
  formData.append("officialTitle", request.officialTitle);
  formData.append("officialSignature", request.officialSignature);
  formData.append("signatureDate", request.signatureDate);
  formData.append("timeZone", request.timeZone);
  authorizationPhotos.forEach((file) => {
    formData.append("authorizationPhotos", file);
  });
  return apiClient.post("/forms/shift-overlap-authorization", formData);
};

const formsService = {
  submitVehicleServiceUpdate,
  submitRideAlong,
  submitOvertimeRequest,
  getOpenShifts,
  getShiftTypes,
  submitShiftClosure,
  submitReceipt,
  submitFuelCard,
  submitShiftOverlapAuthorization,
};

export default formsService;
